import {FeatureAppLoader} from '@feature-hub/react';
import type {SmartEvt10Product} from '@smart/analytics';
import {
  useFeatureFlags,
  usePriceType,
  useSalesChannel,
} from '@smart/feature-app/react';
import type {PaymentConfigV1} from '@smart/payment-config-service';
import type {TagProps} from '@smart/react-components';
import {Button, Dialog, Flex, Tag} from '@smart/react-components';
import {SalesChannel} from '@smart/types';
import {
  DynamicMediaPicture,
  Heading,
  IconButton,
  LegalDisclaimer,
  Text,
} from '@smart/web-components';
import React, {useRef, useState} from 'react';
import type {BadgeTypes} from '../../../bff/types';
import {UsedFeatureFlags} from '../../constants/common';
import {useAppModel} from '../../contexts/app-model-context';
import {useLocale} from '../../hooks/use-locale';
import {
  EventName,
  createNavigationEvent,
  useTracking,
} from '../../hooks/use-tracking';
import type {ProductCardProps} from '../../utils/create-product-card-props';
import {HighlightList} from '../highlightlist';
import {TaxLabel} from '../product-card/tax-label';
import styles from './index.module.scss';
export type PriceKey = 'leasingPrice' | 'totalPrice';

/* eslint-disable complexity */
export const ProductCard = ({
  id,
  imageUrl,
  brandName: brandText,
  headline,
  highlights,
  stockLevelText,
  stockLevelStatus,
  leasingPrice: defaultLeasingPrice,
  calculatedLeasingPrice,
  totalPrice: defaultTotalPrice,
  modelYear,
  totalPriceRaw,
  calculatedTotalPrice,
  defaultTotalPriceLabel,
  adjustedTotalPriceLabel,
  leasingLabel,
  leasingPriceReference,
  buyButtonUrl,
  buyButtonText,
  moreInformationButtonUrl: moreInformationUrl,
  moreInformationButtonText: moreInformationText,
  addToGarageFeatureAppConfig,
  disclaimerRefs,
  legalDisclaimer: legalText,
  paymentConfigService,
  exclVatLabel,
  inclVatLabel,
  cashPriceAdditionalInformation,
  netPrice,
  calculatedNetPrice,
  netLeasingPrice,
  calculatedNetLeasingPrice,
  energyLabelImage,
  productType,
  highlightExteriorColor,
  highlightInteriorColor,
  appMarketConfig,
  index,
  badges,
  showLeasingTools = true,
  isEnergyLabelModalEnabled,
}: ProductCardProps) => {
  const track = useTracking();
  const [modalOpen, setModalOpen] = useState(false);
  const [priceType] = usePriceType();
  const {marketId} = useLocale();
  const {enabled: enableModelYear} = useFeatureFlags(
    UsedFeatureFlags.ENABLE_MODEL_YEAR,
  );
  const salesChannel = useSalesChannel();
  const {appModel} = useAppModel();

  const isInStock = stockLevelStatus === `inStock`;
  const isSME = priceType === `commercial`;
  const showNetPrice = isSME && !appMarketConfig.showOnlyTotalGrossPrice;

  const {enabled: hidePaymentCalculator} = useFeatureFlags(
    UsedFeatureFlags.HIDE_PAYMENT_CALCULATOR,
  );

  const handleAdjustPaymentClick = React.useCallback(
    () =>
      paymentConfigService.openCalculator({
        purchaseItem: {type: `pn18`, pn18: id},
        initialConfig: paymentConfigService.getConfig(),
      }),
    [id, paymentConfigService],
  );

  const pictureRef = useRef<HTMLDivElement | null>(null);

  const handleDisclaimersClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if (
      pictureRef.current &&
      pictureRef.current.contains(event.target as Node)
    ) {
      setModalOpen(true);
    }
  };

  const [paymentConfig] = React.useState<PaymentConfigV1 | undefined>(
    paymentConfigService.getConfig(),
  );

  const productForTracking: SmartEvt10Product = {
    id,
    name: headline,
    type: productType,
    priceUnit: totalPriceRaw,
    priceTotal: totalPriceRaw,
    qty: 1,
    inStock: stockLevelStatus,
    model: brandText,
    index: `${index}`,
  };

  const buyButtonHref = `${buyButtonUrl}?pn18=${id}&addToCart=true`;
  const moreInfoButtonHref = `${moreInformationUrl}?pn18=${id}`;

  const handleBuyButtonClick = () => {
    track.event(
      createNavigationEvent({
        destinationUrl: window.location.origin + buyButtonHref,
        url: window.location.href,
        name: EventName.ADD_TO_CART,
        product: [productForTracking],
      }),
    );
  };

  const handleMoreInformationButtonClick = () => {
    track.event(
      createNavigationEvent({
        destinationUrl: window.location.origin + moreInfoButtonHref,
        url: window.location.href,
        name: EventName.MORE_INFO,
        product: [productForTracking],
      }),
    );
  };

  const getPrice = (key: PriceKey, isNetPrice: boolean) => {
    const netPrices = {
      leasingPrice:
        (calculatedNetLeasingPrice?.replace(`-`, ``)
          ? calculatedNetLeasingPrice
          : netLeasingPrice) || `-`,
      totalPrice: calculatedNetPrice?.replace(`-`, ``)
        ? calculatedNetPrice
        : netPrice,
    };
    if (isNetPrice) {
      return netPrices[key];
    }
    const grossPrices = {
      leasingPrice:
        (calculatedLeasingPrice?.replace(`-`, ``)
          ? calculatedLeasingPrice
          : defaultLeasingPrice) || `-`,
      totalPrice: calculatedTotalPrice?.replace(`-`, ``)
        ? calculatedTotalPrice
        : defaultTotalPrice,
    };

    return grossPrices[key];
  };

  const isPCPPayment =
    marketId.toLowerCase() === `gb` && paymentConfig?.mode === `finance`;
  const showVatLabel = !isPCPPayment;

  const vatLabel = showNetPrice ? exclVatLabel : inclVatLabel;

  const variants: Record<BadgeTypes, TagProps['variant']> = {
    PRIMARY: `primary`,
    GHOST: `inverted`,
    GRAY: `grey`,
    LIME: `lime`,
  };

  const smallEnergyLabelImageUrl = energyLabelImage?.replace(`{{PN18}}`, id);
  const largeEnergyLabelImageUrl = energyLabelImage
    ? smallEnergyLabelImageUrl + `?&wid=1000&fmt=png&resMode=sharp`
    : undefined;
  const disclaimers = legalText
    ? [
        {
          legalText,
          image: smallEnergyLabelImageUrl
            ? {
                imageAltText: `Smartcar energy label`,
                imageUrl: smallEnergyLabelImageUrl,
              }
            : undefined,
          className: `${styles[`legalDisclaimer`]} ${
            isEnergyLabelModalEnabled
              ? styles[`legalDisclaimer--with-pointer`]
              : styles[`legalDisclaimer--without-pointer`]
          }`,
        },
      ]
    : undefined;

  const moreInfoButton = React.useMemo<JSX.Element | null>(
    () =>
      salesChannel !== SalesChannel.DEMO ? (
        <Button
          as="a"
          href={moreInfoButtonHref}
          variant="secondary"
          mode={200}
          onClick={handleMoreInformationButtonClick}
        >
          {moreInformationText}
        </Button>
      ) : null,
    [
      salesChannel,
      moreInfoButtonHref,
      handleMoreInformationButtonClick,
      moreInformationText,
    ],
  );

  return (
    <div className={styles.productCard}>
      {badges.length > 0 && (
        <Flex
          className={styles[`tag-container`]}
          direction="row"
          gap={100}
          wrap="wrap"
        >
          {badges.map((badge) => (
            <Tag key={badge.code} variant={variants[badge.type]}>
              {badge.name}
              {appModel?.badgeFootnotes?.[`badge/${badge.code}`] && (
                <sup className={styles[`footnotes`]}>
                  {appModel?.badgeFootnotes?.[`badge/${badge.code}`]}
                </sup>
              )}
            </Tag>
          ))}
        </Flex>
      )}

      {addToGarageFeatureAppConfig && (
        <div className={styles.likeIcon}>
          <FeatureAppLoader
            key={`${addToGarageFeatureAppConfig.id}-${id}`}
            featureAppId={`${addToGarageFeatureAppConfig.id}-${id}`}
            featureAppName={addToGarageFeatureAppConfig.featureAppName}
            baseUrl={addToGarageFeatureAppConfig.baseUrl}
            src={addToGarageFeatureAppConfig.src}
            config={{productId: id, variant: `icon-button`}}
          />
        </div>
      )}
      {imageUrl && (
        <div className={styles.productCardImageContainer}>
          <DynamicMediaPicture
            className={styles.productCardImage}
            imageUrl={imageUrl}
            imageConfigs={[{sizes: `400px`, aspectRatio: `16-9`}]}
            imageAltText={`smart #1`}
            hasImageTransparency
            noLoadingLogo
          />
        </div>
      )}
      <div>
        <Text variant={`paragraph-100`} className={styles.productCardBrand}>
          {brandText}
          {enableModelYear && modelYear ? ` | ${modelYear}` : ``}
        </Text>
      </div>

      <Heading
        className={styles.productCardHeadline}
        variant="headline-functional-100"
      >
        {headline}
      </Heading>
      <div className={styles.stockLevelContainer}>
        <Text className={`${styles.productCardStockLevel}`}>
          {stockLevelText.stockText}
        </Text>
        {stockLevelText.deliveryText && (
          <Text
            tag="span"
            className={`${styles.productCardStockLevel} ${
              styles[`productCardStockLevel--inStock`]
            }`}
          >
            {stockLevelText.deliveryText}
            {disclaimerRefs.delivery && <sup>{disclaimerRefs.delivery}</sup>}
          </Text>
        )}
      </div>
      <HighlightList
        id={id}
        listItems={highlights}
        disclaimerRefs={disclaimerRefs}
        exteriorLabel={highlightExteriorColor}
        interiorLabel={highlightInteriorColor}
      />
      {showLeasingTools ? (
        <>
          <div className={styles.productCardPriceWrapper}>
            <div className={styles.productCardBuyPriceWrapper}>
              <div className={styles.productCardPriceContainerTotal}>
                <Text
                  variant="paragraph-100"
                  className={styles.productCardPriceText}
                >
                  {calculatedTotalPrice
                    ? adjustedTotalPriceLabel
                    : defaultTotalPriceLabel}
                  {disclaimerRefs.price && <sup>{disclaimerRefs.price}</sup>}
                </Text>
                <div className={styles.productCardPriceBox}>
                  <Text tag={`span`} className={styles.productCardPriceValue}>
                    {getPrice(`totalPrice`, showNetPrice)}
                  </Text>
                  <TaxLabel
                    deprecatedVatLabel={vatLabel}
                    priceKey="totalPrice"
                    priceType={priceType}
                    version="v2"
                  />
                </div>
              </div>
            </div>

            {salesChannel !== SalesChannel.DEMO && (
              <>
                <div className={styles.productCardPriceContainer}>
                  <Text
                    variant="paragraph-100"
                    className={styles.productCardPriceText}
                  >
                    {leasingLabel}
                    <sup>{leasingPriceReference}</sup>
                  </Text>
                  <div className={styles.productCardPriceBox}>
                    <Text tag="span" className={styles.productCardLeasingValue}>
                      {getPrice(`leasingPrice`, isSME)}
                    </Text>
                    {showVatLabel && (
                      <TaxLabel
                        deprecatedVatLabel={vatLabel}
                        priceKey="leasingPrice"
                        priceType={priceType}
                        version="v2"
                      />
                    )}
                  </div>
                </div>

                <div className={styles.productCardPaymentLink}>
                  <IconButton
                    variant="ghost"
                    size="200"
                    iconName="info-legal--small"
                    ariaLabel="legal note icon"
                    onClick={handleAdjustPaymentClick}
                  />
                  {!hidePaymentCalculator && (
                    <IconButton
                      variant="ghost"
                      size="200"
                      iconName="calculator--small"
                      ariaLabel="payment calculator"
                      onClick={handleAdjustPaymentClick}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {!!cashPriceAdditionalInformation &&
            cashPriceAdditionalInformation.length > 1 && (
              <div className={styles.paymentCalculatorContainer}>
                <Text
                  className={styles.productCardAdittionalCashPriceInformation}
                  variant={`paragraph-200`}
                >
                  {cashPriceAdditionalInformation}
                </Text>
              </div>
            )}

          <div className={styles.productCardBottomButtonContainer}>
            <Button
              as="a"
              href={buyButtonHref}
              variant="primary"
              mode={200}
              disabled={!isInStock}
              onClick={handleBuyButtonClick}
            >
              {buyButtonText}
            </Button>
            {moreInfoButton}
          </div>
        </>
      ) : (
        <>
          <div className={styles.productCardPriceWrapper}>
            <div className={styles.productCardBuyPriceWrapper}>
              <Text
                className={styles.productCardLeasingPrice}
                variant="paragraph-100"
              >
                {calculatedTotalPrice
                  ? adjustedTotalPriceLabel
                  : defaultTotalPriceLabel}

                {disclaimerRefs.price && <sup>{disclaimerRefs.price}</sup>}
                <Text tag={`span`} className={styles.productCardPriceBox}>
                  <Heading
                    tag="span"
                    variant="unstyled"
                    className={styles.productCardNetPrice}
                  >
                    {getPrice(`totalPrice`, showNetPrice)}
                  </Heading>
                  <TaxLabel
                    deprecatedVatLabel={vatLabel}
                    priceKey="totalPrice"
                    priceType={priceType}
                    version="v2"
                  />
                </Text>
              </Text>
            </div>
          </div>
          <div className={styles.productCardBottomButtonContainer}>
            <Button
              as="a"
              href={buyButtonHref}
              variant="primary"
              mode={200}
              disabled={!isInStock}
              onClick={handleBuyButtonClick}
            >
              {buyButtonText}
            </Button>
            {moreInfoButton}
          </div>
          <hr className={styles.productCardDividerBottom} />
        </>
      )}

      {disclaimers && (
        <>
          <div
            className={styles[`parentLegalDisclaimer`]}
            onClick={handleDisclaimersClick}
            ref={pictureRef}
          >
            <LegalDisclaimer legalDisclaimers={disclaimers} />
          </div>
          {isEnergyLabelModalEnabled && (
            <Dialog open={modalOpen}>
              <Dialog.Headline>
                <Flex direction="row-reverse">
                  <IconButton
                    variant="ghost"
                    size="200"
                    iconName="close-without-circle--medium"
                    ariaLabel="close button"
                    onClick={() => setModalOpen(false)}
                  />
                </Flex>
              </Dialog.Headline>
              <Dialog.Text>
                <img
                  src={largeEnergyLabelImageUrl}
                  alt="Detailed Energy Label for smartcar"
                  className={styles.legalDisclaimerImage}
                />
              </Dialog.Text>
            </Dialog>
          )}
        </>
      )}
    </div>
  );
};
